import React, { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useField, useFormikContext } from 'formik';
import { Camera } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CameraRBModal } from './NativeCamera';

const CompletionPhotoField: React.FC = () => {
    const { i18n } = useTranslation();
    // i18next's newer types are incompatible with this app's TypeScript 4 compiler.
    const t = i18n.t as unknown as (key: string) => string;
    const [field, meta, helpers] = useField<File | null>('completion_photo');
    const { isSubmitting } = useFormikContext();
    const [showCamera, setShowCamera] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    useEffect(() => {
        if (!field.value || !field.value.type.startsWith('image/')) {
            setPreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(field.value);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [field.value]);

    return (
        <Form.Group className="mt-3" controlId="completion-photo">
            <Form.Label>{t('completion_photo')}</Form.Label>
            <Form.Control
                type="file"
                name={field.name}
                accept="image/jpeg,image/png,image/gif,image/webp,image/bmp"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => helpers.setValue(event.currentTarget.files?.[0] ?? null)}
                isInvalid={Boolean(meta.error)}
                disabled={isSubmitting}
            />
            <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
            <Button type="button" variant="outline-primary" className="mt-2" disabled={isSubmitting} onClick={() => setShowCamera(true)}>
                <Camera size={20} className="me-2" aria-hidden="true" />
                {t('completion_photo_take_picture')}
            </Button>
            <Form.Text className="text-muted d-block">{t('completion_photo_help')}</Form.Text>
            {previewUrl && !meta.error && (
                <img src={previewUrl} alt={t('completion_photo')} className="d-block mt-2" style={{ maxWidth: '100%', maxHeight: 200 }} />
            )}
            <CameraRBModal
                show={showCamera}
                onHide={() => setShowCamera(false)}
                name={field.name}
                toFilename="completion-photo.jpg"
                title={t('completion_photo_take_picture')}
                onCapture={() => setShowCamera(false)}
                commitOnUse
            />
        </Form.Group>
    );
};

export default CompletionPhotoField;
